import { useState } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, LogIn } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const success = await login(email, password);
    
    if (success) {
      navigate("/dashboard");
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Login Form */}
      <motion.div 
        className="w-full lg:w-[45%] flex items-center justify-center bg-background p-8 lg:p-12"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full max-w-md space-y-8">
          {/* Logo & Header */}
          <div className="space-y-2">
            <motion.div 
              className="flex items-center gap-3 mb-8"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <img 
                src="https://maxvalle.it/wp-content/uploads/2026/01/verticale-arancio-scuro-2.png" 
                alt="Max Valle Logo" 
                className="h-14 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">Marketing Max Valle</h1>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Lead Generation Platform</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-3xl font-bold tracking-tight text-foreground">Bentornato</h2>
              <p className="text-muted-foreground mt-2">
                Accedi alla piattaforma di marketing
              </p>
            </motion.div>
          </div>

          {/* Login Form */}
          <motion.form 
            onSubmit={handleSubmit}
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@esempio.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="h-12 input-animate"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="h-12 pr-12 input-animate"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-animate text-base font-medium"
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? (
                <div className="spinner !w-5 !h-5 !border-primary-foreground !border-t-transparent" />
              ) : (
                <>
                  <LogIn className="mr-2 h-5 w-5" />
                  Accedi
                </>
              )}
            </Button>
          </motion.form>

          {/* Footer */}
          <motion.p 
            className="text-center text-xs text-muted-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Accesso riservato agli utenti autorizzati
          </motion.p>
        </div>
      </motion.div>

      {/* Right Panel - Image */}
      <motion.div 
        className="hidden lg:block lg:w-[55%] relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <img
          src="https://images.unsplash.com/photo-1657727534685-36b09f84e193?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc2OTM0ODA2MHww&ixlib=rb-4.1.0&q=85"
          alt="Digital Marketing"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="login-image-overlay" />
        
        {/* Overlay Content */}
        <div className="absolute inset-0 flex items-end p-12">
          <motion.div 
            className="backdrop-blur-md bg-white/80 rounded-2xl p-8 max-w-md shadow-lg"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <blockquote className="space-y-4">
              <p className="text-lg font-medium text-foreground leading-relaxed">
                "Il marketing non riguarda le cose che fai, ma le storie che racconti."
              </p>
              <footer className="text-sm text-muted-foreground">
                — Seth Godin
              </footer>
            </blockquote>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
